import React, { useState, useMemo, useEffect } from "react";
import { Box, Button, TextField, MenuItem, Select, FormControl, InputAdornment, IconButton } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { axios_tenant } from "../../../../axios";
import { toast } from "react-toastify";
import { ENTITY_STATUS_OPTIONS, formatEntityStatus, matchesEntityStatusFilter } from "../utils/entityStatus";
import { formatKotDate } from "../utils/dateHelpers";
import { clientSortComparator, toSortableDate } from "../utils/clientSort";
import TruncatedText from "./TruncatedText";

const ClientTable = ({ showFavoritesOnly = false }) => {
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [searchType, setSearchType] = useState("all");
  const [entityFilter, setEntityFilter] = useState("");
  const [clientFilter, setClientFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [accessLoading, setAccessLoading] = useState(false);
  const [myAccess, setMyAccess] = useState(null);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const fetchClients = async () => {
    try {
      setLoading(true);

      const response = await axios_tenant.get("/kot/client-profiles", {
        params: { status: "ACTIVE" },
      });

      let data = response?.data?.content || [];

      if (showFavoritesOnly) {
        data = data.filter((c) => c.isFavorite);
      }

      setClients(data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, [showFavoritesOnly]);

  useEffect(() => {
    const fetchMyAccess = async () => {
      try {
        setAccessLoading(true);
        const response = await axios_tenant.get("/kot/me/access");
        setMyAccess(response?.data || null);
      } catch (error) {
      } finally {
        setAccessLoading(false);
      }
    };

    fetchMyAccess();
  }, []);

  const handleToggleFavourite = async (id, isFavourite) => {
    try {
      let response;

      if (isFavourite) {
        response = await axios_tenant.delete(`/kot/client-profiles/${id}/favorite`);
      } else {
        response = await axios_tenant.post(`/kot/client-profiles/${id}/favorite`);
      }

      if (response?.data?.success) {
        toast.success(response?.data?.message || `Client ${isFavourite ? "removed from favorites" : "marked as favorite"}.`);
        await fetchClients();
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to update favorite status.");
    }
  };

  const filteredClients = useMemo(() => {
    return clients.filter((client) => {
      const clientName = client.clientName || client.clientLegalName || client.name || "";
      const clientNumber = client.clientNumber || "";
      let matchesSearch = true;

      if (search) {
        const searchLower = search.toLowerCase();

        if (searchType === "all") {
          matchesSearch = clientName.toLowerCase().includes(searchLower) || clientNumber.toLowerCase().includes(searchLower);
        }

        if (searchType === "name") {
          matchesSearch = clientName.toLowerCase().includes(searchLower);
        }

        if (searchType === "number") {
          matchesSearch = clientNumber.toLowerCase().includes(searchLower);
        }
      }

      const matchesEntity = matchesEntityStatusFilter(client.entityStatus, entityFilter);


      return matchesSearch && matchesEntity;
    });
  }, [clients, search, searchType, entityFilter]);

  const editClient = async (id) => {
    navigate(`/kot/client/edit/${id}`);
  };

  const handleCreateNewClient = () => {
    dispatch({ type: "RESET_CLIENT_FORM" });
    navigate("/kot/client/new");
  };

  const canCreateClient = useMemo(() => {
    const clientDetailsPermissions = myAccess?.permissionsByModel?.ClientProfile;
    return Boolean(clientDetailsPermissions?.canView && clientDetailsPermissions?.canEdit);
  }, [myAccess]);

  const columns = [
    {
      field: "clientName",
      headerName: "Client Name",
      flex: 1,
      // Sort on the text the cell actually renders, not the raw clientName field.
      valueGetter: (params) => params.row.clientName || params.row.clientLegalName || params.row.name || "",
      sortComparator: clientSortComparator,
      renderCell: (params) => (
        <Button
          variant="text"
          onClick={() => editClient(params.row.id)}
          sx={{
            textTransform: "none",
            padding: 0,
            justifyContent: "flex-start",
          }}
        >
          <TruncatedText text={params.row.clientName || params.row.clientLegalName || params.row.name || "-"} maxLength={30} />
        </Button>
      ),
    },
    { field: "clientNumber", headerName: "Client Number", flex: 1, sortComparator: clientSortComparator },
    {
      field: "entityStatus",
      headerName: "Entity Status",
      flex: 1,
      renderCell: (params) => <span>{formatEntityStatus(params.row.entityStatus)}</span>,
    },
    {
      field: "lastUpdated",
      headerName: "Last Updated",
      flex: 1,
      // The payload field is lastUpdatedAt, so without this the column sorts an undefined
      // value on every row and clicking the header does nothing. dateTime because the value
      // carries a time, which decides the order of same-day edits.
      type: "dateTime",
      valueGetter: (params) => toSortableDate(params.row.lastUpdatedAt),
      renderCell: (params) => <span>{formatKotDate(params.row.lastUpdatedAt)}</span>,
    },
    {
      field: "isFavourite",
      headerName: "Favorite",
      width: 130,
      sortable: false,
      renderCell: (params) => <IconButton onClick={() => handleToggleFavourite(params.row.id, params.row.isFavorite)}>{params.row.isFavorite ? <StarIcon color="primary" /> : <StarBorderIcon />}</IconButton>,
    },
  ];

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" mb={2}>
        <Box display="flex">
          <FormControl size="small">
            <Select value={searchType} onChange={(e) => setSearchType(e.target.value)} sx={{ borderRadius: "0", borderTopLeftRadius: "50px", borderBottomLeftRadius: "50px" }} >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="name">Client Name</MenuItem>
              <MenuItem value="number">Client Number</MenuItem>
            </Select>
          </FormControl>

          <TextField
            size="small"
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 0,
                borderTopRightRadius: "50px",
                borderBottomRightRadius: "50px",
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box display="flex" gap={2}>
          <FormControl size="small">
            <Select displayEmpty value={entityFilter} onChange={(e) => setEntityFilter(e.target.value)}>
              <MenuItem value="">Entity Status</MenuItem>
              {ENTITY_STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {!accessLoading && canCreateClient && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateNewClient}>
              Create New Client
            </Button>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          height: 700,
          width: "100%",
          background: "#fff",
          border: "1px solid #ddd",
          borderRadius: 2,
        }}
      >
        <DataGrid
          sx={{
            border: 0,

            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#f5f7fb",
              fontWeight: "bold",
              borderBottom: "1px solid #ddd",
            },

            "& .MuiDataGrid-cell": {
              borderBottom: "1px solid #eee",
              overflow: "hidden",
            },
            "& .MuiDataGrid-cell > span": {
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "100%",
            },

            "& .MuiDataGrid-columnSeparator": {
              visibility: "visible",
            },
            // Custom pagination styles
            ".MuiTablePagination-displayedRows": {
              marginTop: "1em",
              marginBottom: "1em",
            },
            ".MuiTablePagination-selectLabel": {
              marginTop: "1em",
              marginBottom: "1em",
            },
          }}
          rows={filteredClients}
          columns={columns}
          // checkboxSelection
          disableRowSelectionOnClick
          disableColumnFilter
          disableColumnMenu
          pageSize={8}
          rowsPerPageOptions={[8]}
          loading={loading}
        />
      </Box>
    </Box>
  );
};

export default ClientTable;
